import { useSectionAccess } from "../../auth/useSectionAccess";
import { useEffect, useMemo, useState } from "react";
import { formatDateShort } from "../../lib/format";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { tmApi, type TmMeetingView } from "../../api/tm";
import Ic from "../../components/icons.jsx";

type Tab = "upcoming" | "today" | "past";

/**
 * Meetings landing — three time-based tabs (Today / Upcoming / Past) with a
 * KPI strip showing this-week load and a richer per-meeting card that calls
 * out time-of-day, duration, location and the linked project at a glance.
 *
 * No calendar grid — a chronological list is faster to scan when
 * meeting counts are small, and we don't yet have a recurring-meeting model.
 */
export default function TmMeetingsPage() {
  const { canWrite } = useSectionAccess("tm_meetings");
  const { t } = useTranslation();
  const [meetings, setMeetings] = useState<TmMeetingView[] | null>(null);
  const [tab, setTab] = useState<Tab>("upcoming");
  const [search, setSearch] = useState("");

  useEffect(() => {
    void tmApi.listMeetings().then(setMeetings).catch(() => setMeetings([]));
  }, []);

  const now = useMemo(() => new Date(), []);
  const today = useMemo(() => new Date(now.toDateString()), [now]);
  const tomorrow = useMemo(() => {
    const d = new Date(today);
    d.setDate(d.getDate() + 1);
    return d;
  }, [today]);
  const weekEnd = useMemo(() => {
    const d = new Date(today);
    d.setDate(d.getDate() + 7);
    return d;
  }, [today]);

  const buckets = useMemo(() => {
    const all = meetings ?? [];
    let today0 = 0;
    let thisWeek = 0;
    let past = 0;
    let upcoming = 0;
    for (const m of all) {
      const at = new Date(m.scheduledAt);
      if (at.getTime() < now.getTime()) past++;
      else upcoming++;
      if (at >= today && at < tomorrow) today0++;
      if (at >= today && at < weekEnd) thisWeek++;
    }
    return { all: all.length, today: today0, thisWeek, upcoming, past };
  }, [meetings, now, today, tomorrow, weekEnd]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (meetings ?? [])
      .filter((m) => {
        const at = new Date(m.scheduledAt);
        if (tab === "today")    { if (!(at >= today && at < tomorrow)) return false; }
        else if (tab === "upcoming") { if (at.getTime() < now.getTime()) return false; }
        else                    { if (at.getTime() >= now.getTime()) return false; }
        if (q) {
          const hay = `${m.title} ${m.location ?? ""} ${m.projectName ?? ""}`.toLowerCase();
          if (!hay.includes(q)) return false;
        }
        return true;
      })
      .sort((a, b) =>
        tab === "past"
          ? new Date(b.scheduledAt).getTime() - new Date(a.scheduledAt).getTime()
          : new Date(a.scheduledAt).getTime() - new Date(b.scheduledAt).getTime());
  }, [meetings, tab, search, now, today, tomorrow]);

  // Group by date string so the list reads like a calendar without a grid.
  const grouped = useMemo(() => {
    const map = new Map<string, TmMeetingView[]>();
    for (const m of filtered) {
      const key = new Date(m.scheduledAt).toDateString();
      const arr = map.get(key) ?? [];
      arr.push(m);
      map.set(key, arr);
    }
    return Array.from(map.entries());
  }, [filtered]);

  return (
    <div className="fadein" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div className="page-head" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", gap: 12, flexWrap: "wrap" }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, color: "var(--ink)" }}>{t("tm.meetings.title", "Meetings")}</h1>
          <div style={{ fontSize: 13, color: "var(--ink-3)", marginTop: 4 }}>
            {meetings === null
              ? t("common.loading", "Loading…")
              : t("tm.meetings.subtitle", {
                  defaultValue: "{{total}} total · {{upcoming}} upcoming · {{week}} this week",
                  total: buckets.all,
                  upcoming: buckets.upcoming,
                  week: buckets.thisWeek,
                })}
          </div>
        </div>
        {canWrite && <Link to="/tm/meetings/new" className="btn primary" style={{ textDecoration: "none" }}>
          <Ic.plus /> {t("tm.meetings.new", "New meeting")}
        </Link>}
      </div>

      <div style={kpiGrid}>
        <KpiTile label={t("tm.meetings.kpi.today", "Today")}        value={buckets.today}    accent="var(--info)" bg="var(--info-bg)" />
        <KpiTile label={t("tm.meetings.kpi.thisWeek", "This week")}  value={buckets.thisWeek} accent="var(--tone-violet)" bg="var(--tone-violet-bg)" />
        <KpiTile label={t("tm.meetings.kpi.upcoming", "Upcoming")}   value={buckets.upcoming} accent="var(--ok)" bg="var(--ok-bg)" />
        <KpiTile label={t("tm.meetings.kpi.past", "Past")}           value={buckets.past}     accent="var(--ink-3)" bg="var(--surface-muted)" />
      </div>

      <div style={tabBar}>
        {(["upcoming", "today", "past"] as Tab[]).map((k) => (
          <button
            key={k}
            type="button"
            onClick={() => setTab(k)}
            style={{
              padding: "8px 14px",
              border: "none",
              borderBottom: tab === k ? "2px solid var(--brand-primary)" : "2px solid transparent",
              background: "transparent",
              color: tab === k ? "var(--ink)" : "var(--ink-3)",
              fontSize: 14,
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            {tabLabel(k, t)}
            <span style={tabCount}>{buckets[k]}</span>
          </button>
        ))}
      </div>

      <div style={searchWrap}>
        <span className="ord-search-icon" style={searchIcon}><Ic.search /></span>
        <input
          className="input"
          placeholder={t("tm.meetings.searchPlaceholder", "Search title, location, project…")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ paddingLeft: 32, height: 36, width: "100%" }}
        />
      </div>

      {meetings === null ? (
        <div style={{ color: "var(--ink-3)" }}>{t("common.loading", "Loading…")}</div>
      ) : filtered.length === 0 ? (
        <div style={emptyState}>
          <div style={{ fontSize: 28, marginBottom: 4 }}>📅</div>
          <b style={{ color: "var(--ink-2)" }}>
            {tab === "upcoming"
              ? t("tm.meetings.empty.upcoming", "No upcoming meetings.")
              : tab === "today"
                ? t("tm.meetings.empty.today", "Nothing scheduled for today.")
                : t("tm.meetings.empty.past", "No past meetings to show.")}
          </b>
          {tab !== "past" && canWrite && (
            <Link to="/tm/meetings/new" className="btn primary" style={{ textDecoration: "none", marginTop: 12 }}>
              <Ic.plus /> {t("tm.meetings.new", "New meeting")}
            </Link>
          )}
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {grouped.map(([dateKey, items]) => (
            <section key={dateKey}>
              <h2 style={dayHead}>{fmtDay(new Date(dateKey), today, t)}</h2>
              <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 6 }}>
                {items.map((m) => <MeetingRow key={m.id} m={m} now={now} />)}
              </ul>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}

function MeetingRow({ m, now }: { m: TmMeetingView; now: Date }) {
  const { t } = useTranslation();
  const start = new Date(m.scheduledAt);
  const end = new Date(start.getTime() + (m.durationMinutes ?? 30) * 60_000);
  const happening = start <= now && now < end;
  const past = end <= now;
  return (
    <li>
      <Link to={`/tm/meetings/${m.id}`} style={{ textDecoration: "none", color: "inherit" }}>
        <article style={{
          ...meetingRow,
          ...(happening ? { boxShadow: "inset 3px 0 0 var(--ok), var(--shadow-1)" } : {}),
          ...(past ? { opacity: 0.7 } : {}),
        }}>
          <div style={timeBlock}>
            <div style={{ fontSize: 14, fontWeight: 700, color: "var(--ink)", fontVariantNumeric: "tabular-nums" }}>
              {fmtTime(start)}
            </div>
            <div style={{ fontSize: 11, color: "var(--ink-4)" }}>
              {t("tm.meetings.minutes", { defaultValue: "{{count}} min", count: m.durationMinutes })}
            </div>
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 600, color: "var(--ink)", fontSize: 14, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {m.title}
              {happening && <span style={livePill}>{t("tm.meetings.live", "LIVE")}</span>}
              {(m.recurrenceRule || m.recurrenceParentId) && (
                <span style={{ marginLeft: 8, fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 999, background: "var(--tone-violet-bg)", color: "var(--tone-violet)", letterSpacing: 0.5, textTransform: "uppercase" }}>
                  ↻ {t("tm.meetings.recurring", "Recurring")}
                </span>
              )}
            </div>
            <div style={{ fontSize: 12, color: "var(--ink-3)", marginTop: 2, display: "flex", gap: 12, flexWrap: "wrap" }}>
              {m.location && <span><Ic.world /> {m.location}</span>}
              {m.projectName && <span><Ic.book /> {m.projectName}</span>}
              <span>{t("tm.meetings.by", { defaultValue: "by {{name}}", name: m.createdByName })}</span>
            </div>
          </div>
        </article>
      </Link>
    </li>
  );
}

function KpiTile({ label, value, accent, bg }: { label: string; value: number; accent: string; bg: string }) {
  return (
    <div style={{
      background: "var(--surface)", border: "1px solid var(--border)",
      borderRadius: 10, padding: "12px 14px",
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span style={{ fontSize: 11, color: "var(--ink-3)", textTransform: "uppercase", letterSpacing: 0.4 }}>{label}</span>
        <span style={{ width: 8, height: 8, borderRadius: 999, background: accent, boxShadow: `0 0 0 4px ${bg}` }} />
      </div>
      <div style={{ fontSize: 22, fontWeight: 700, color: "var(--ink)", marginTop: 4, fontVariantNumeric: "tabular-nums" }}>
        {value}
      </div>
    </div>
  );
}

function tabLabel(tab: Tab, t: TFunction): string {
  if (tab === "upcoming") return t("tm.meetings.tab.upcoming", "Upcoming");
  if (tab === "today") return t("tm.meetings.tab.today", "Today");
  return t("tm.meetings.tab.past", "Past");
}

function fmtDay(d: Date, today: Date, t: TFunction): string {
  const diff = Math.round((d.getTime() - today.getTime()) / 86400000);
  const weekday = formatDateShort(d);
  const date = formatDateShort(d);
  const full = `${weekday}, ${date}`;
  if (diff === 0)  return t("tm.meetings.day.today", { defaultValue: "Today · {{date}}", date: full });
  if (diff === 1)  return t("tm.meetings.day.tomorrow", { defaultValue: "Tomorrow · {{date}}", date: full });
  if (diff === -1) return t("tm.meetings.day.yesterday", { defaultValue: "Yesterday · {{date}}", date: full });
  return full;
}

function fmtTime(d: Date): string {
  return d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
}

const tabBar: React.CSSProperties = { display: "flex", gap: 8, borderBottom: "1px solid var(--border)" };
const tabCount: React.CSSProperties = { marginLeft: 6, padding: "1px 7px", borderRadius: 999, background: "var(--surface-2)", fontSize: 11, fontWeight: 600 };
const kpiGrid: React.CSSProperties = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 10 };
const searchWrap: React.CSSProperties = { position: "relative", display: "flex", alignItems: "center", maxWidth: 380 };
const searchIcon: React.CSSProperties = { position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", width: 16, height: 16, display: "inline-flex", color: "var(--ink-3)", pointerEvents: "none" };
const dayHead: React.CSSProperties = { margin: "0 0 8px", fontSize: 12, color: "var(--ink-3)", textTransform: "uppercase", letterSpacing: 0.4, fontWeight: 700 };
const meetingRow: React.CSSProperties = { background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 10, padding: 12, display: "flex", alignItems: "center", gap: 14, boxShadow: "var(--shadow-1)", transition: "transform .12s ease, box-shadow .12s ease" };
const timeBlock: React.CSSProperties = { minWidth: 64, padding: "6px 10px", background: "var(--surface-2)", border: "1px solid var(--border)", borderRadius: 8, textAlign: "center" };
const livePill: React.CSSProperties = { marginLeft: 8, fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 999, background: "var(--ok-bg)", color: "var(--ok)", letterSpacing: 0.5, textTransform: "uppercase" };
const emptyState: React.CSSProperties = { background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 10, padding: 32, textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center" };
